import { useState } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import { Copy } from 'lucide-react';
import type { CreateApiTokenDTO, GeneratedApiToken } from '../types/apiToken.types';
import { useTranslation } from 'react-i18next';

interface CreateTokenDialogProps {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  generatedToken: GeneratedApiToken | null;
  onCreate: (payload: CreateApiTokenDTO) => Promise<void>;
  onCopy: (token: string) => void;
  isCreating: boolean;
}

export function CreateTokenDialog({
  workspaceId,
  open,
  onOpenChange,
  generatedToken,
  onCreate,
  onCopy,
  isCreating
}: CreateTokenDialogProps) {
  const { t } = useTranslation();
  const [tokenName, setTokenName] = useState('');
  const [hasExpiration, setHasExpiration] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');

  const handleCreate = async () => {
    const payload: CreateApiTokenDTO = {
      product_id: workspaceId,
      token_name: tokenName.trim(),
      expires_at: hasExpiration && expiresAt ? new Date(expiresAt).toISOString() : null
    };
    await onCreate(payload);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setTokenName('');
      setHasExpiration(false);
      setExpiresAt('');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('workspaces.apiTokens.createTitle')}</DialogTitle>
          <DialogDescription>
            {generatedToken
              ? t('workspaces.apiTokens.createSuccessDesc')
              : t('workspaces.apiTokens.createDesc')}
          </DialogDescription>
        </DialogHeader>

        {!generatedToken ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="token_name">{t('workspaces.apiTokens.tokenName')}</Label>
              <Input
                id="token_name"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                placeholder={t('workspaces.apiTokens.tokenNamePlaceholder')}
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={hasExpiration} onCheckedChange={setHasExpiration} />
              <Label>{t('workspaces.apiTokens.setExpiration')}</Label>
            </div>

            {hasExpiration ? (
              <div className="space-y-2">
                <Label htmlFor="expires_at">{t('workspaces.apiTokens.expiresAt')}</Label>
                <Input
                  id="expires_at"
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-3">
            <Label>{t('workspaces.apiTokens.yourToken')}</Label>
            <div className="flex gap-2">
              <Input value={generatedToken.token} readOnly className="font-mono text-sm" />
              <Button variant="outline" size="icon" onClick={() => onCopy(generatedToken.token)} title={t('workspaces.apiTokens.copyToken')}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('workspaces.apiTokens.copyWarning')}
            </p>
          </div>
        )}

        <DialogFooter>
          {!generatedToken ? (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!tokenName.trim() || isCreating}
              >
                {t('common.create')}
              </Button>
            </>
          ) : (
            <Button onClick={() => handleOpenChange(false)}>{t('common.done')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
